import {
  COMPANY_BRAND, getLogoBase64, genLabel, captureChartImage,
  BRAND_NAVY_RGB, BRAND_GREEN_RGB, SEMAFORO_RGB, SEMAFORO_ARGB, SEMAFORO_TINT_ARGB,
  BRAND_NAVY_ARGB, BRAND_NAVY_LIGHT_ARGB,
  drawPdfHeader, drawPdfFooter, drawSectionBarPdf, drawKpiCardsPdf, addChartImagePdf, ensurePdfSpace,
  xlsxSectionHeader, xlsxKpiCards, addChartImageXlsx,
} from '../utils/brandAssets';

// Mantido por compatibilidade com o texto já usado nesta página (rodapés/
// títulos) — mesma marca de `COMPANY_BRAND`, importado de brandAssets.js
// (fonte única de identidade visual, ver [[export-and-scope-patterns]]).
const COMPANY = COMPANY_BRAND;
// Cores do semáforo (verde/vermelho) usadas aqui pra "OK"/alerta CLT —
// mesma paleta de `SEMAFORO_ARGB`/`SEMAFORO_RGB`, não redefinir hex local.
const RED_ARGB = SEMAFORO_ARGB.vermelho;
const GREEN_ARGB = SEMAFORO_ARGB.verde;
const RED_RGB = SEMAFORO_RGB.vermelho;
const GREEN_RGB = SEMAFORO_RGB.verde;

// ─── Formatadores ─────────────────────────────────────────────────────────────

function fmtDateBR(str) {
  if (!str) return '';
  return new Date(str + 'T12:00:00').toLocaleDateString('pt-BR');
}

function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function periodLabel(filters) {
  const f = filters.date_from ? fmtDateBR(filters.date_from) : '—';
  const t = filters.date_to   ? fmtDateBR(filters.date_to)   : '—';
  return `Período: ${f} a ${t}`;
}

// ─── Cabeçalhos padronizados ──────────────────────────────────────────────────

export const DAILY_HEADERS = [
  'Funcionário', 'Departamento', 'Data',
  'Entrada', 'Saída p/ Almoço', 'Retorno do Almoço', 'Saída Geral',
  'Total Trabalhado', 'Horas Extras', 'Intervalo Almoço', 'Ocorrências CLT',
];

export const WEEKLY_HEADERS = [
  'Funcionário', 'Departamento', 'Semana (ISO)',
  'Dias Trabalhados', 'Total Trabalhado',
  'H.E. Diárias (>8h/dia)', 'H.E. Semanais (>44h/sem)',
  'Total Horas Extras', 'Alertas CLT',
];

export const MONTHLY_HEADERS = [
  'Funcionário', 'Departamento', 'Matrícula', 'Dias Trabalhados',
  'Total Trabalhado', 'H.E. Diárias (>8h/dia)', 'H.E. Semanais (>44h/sem)',
  'Total Horas Extras', 'Trabalho Noturno (dias)', 'Alertas CLT',
];

// ─── Extração de linhas ───────────────────────────────────────────────────────

function dailyRows(records) {
  return records.map((r) => [
    r.employee?.full_name          || '—',
    r.employee?.department?.name   || '—',
    r.work_date ? fmtDateBR(r.work_date) : '—',
    fmtTime(r.clock_in),
    fmtTime(r.lunch_out),
    fmtTime(r.lunch_in),
    fmtTime(r.clock_out),
    r.incomplete ? 'Em aberto' : (r.worked_fmt  || '—'),
    (r.overtime_minutes > 0) ? r.overtime_fmt : '—',
    r.lunch_fmt  || '—',
    r.violations?.length
      ? r.violations.join('; ')
      : (r.has_night_work ? 'Trabalho noturno (22h–5h)' : 'OK'),
  ]);
}

function weeklyRows(reportData) {
  return reportData.flatMap((emp) =>
    (emp.weeks || []).map((wk) => [
      emp.full_name,
      emp.department,
      wk.week,
      wk.days,
      wk.worked_fmt,
      wk.daily_overtime_minutes  > 0 ? wk.daily_overtime_fmt  : '—',
      wk.weekly_overtime_minutes > 0 ? wk.weekly_overtime_fmt : '—',
      wk.total_overtime_minutes  > 0 ? wk.total_overtime_fmt  : '—',
      wk.violations > 0 ? `${wk.violations} alerta(s)` : 'OK',
    ])
  );
}

function monthlyRows(reportData) {
  return reportData.map((emp) => {
    const s = emp.summary;
    return [
      emp.full_name,
      emp.department,
      emp.registration || '—',
      s.complete_records,
      s.total_worked_fmt,
      s.daily_overtime_minutes  > 0 ? s.daily_overtime_fmt  : '—',
      s.weekly_overtime_minutes > 0 ? s.weekly_overtime_fmt : '—',
      s.total_overtime_minutes  > 0 ? s.total_overtime_fmt  : '—',
      s.night_work_days > 0 ? s.night_work_days : '—',
      s.total_violations > 0 ? `${s.total_violations} alerta(s)` : 'OK',
    ];
  });
}

// ─── Exportação Excel ─────────────────────────────────────────────────────────

// Colunas cujo texto (mesma lógica condicional do PDF via didParseCell)
// recebe realce vermelho quando houver valor, e a coluna de alertas/
// ocorrências recebe verde para "OK" e vermelho quando houver alerta real.
function styleDataCell(cell, header, value) {
  const val = String(value ?? '');
  if (OVERTIME_COLS.includes(header) && val !== '—' && val !== '') {
    cell.font = { color: { argb: RED_ARGB }, bold: true };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SEMAFORO_TINT_ARGB.vermelho } };
  } else if ((header.includes('Alerta') || header.includes('Ocorrência'))) {
    if (val === 'OK') {
      cell.font = { color: { argb: GREEN_ARGB } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SEMAFORO_TINT_ARGB.verde } };
    } else if (val !== '—' && val !== '') {
      cell.font = { color: { argb: RED_ARGB }, bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SEMAFORO_TINT_ARGB.vermelho } };
    }
  }
}

async function downloadWorkbook(wb, filename) {
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function exportExcel(records, reportData, filters) {
  // Carga dinâmica para não inflar o bundle inicial (mesmo padrão já usado
  // aqui para jspdf/jspdf-autotable).
  const { default: ExcelJS } = await import('exceljs');

  const wb  = new ExcelJS.Workbook();
  wb.creator = COMPANY;
  wb.created = new Date();

  const per = periodLabel(filters);
  const gen = genLabel();
  const logoB64 = await getLogoBase64();

  const sheets = [
    { name: 'Registros Diários', headers: DAILY_HEADERS,   rows: dailyRows(records) },
    { name: 'Resumo Semanal',    headers: WEEKLY_HEADERS,  rows: weeklyRows(reportData) },
    { name: 'Resumo Mensal',     headers: MONTHLY_HEADERS, rows: monthlyRows(reportData) },
  ];

  for (const s of sheets) {
    const ws = wb.addWorksheet(s.name);
    let r = 1;

    if (logoB64) {
      const imgId = wb.addImage({ base64: logoB64, extension: 'jpeg' });
      ws.addImage(imgId, { tl: { col: 0, row: 0 }, ext: { width: 110, height: 62 } });
      r = 6;
    }

    ws.getCell(r, 1).value = COMPANY;
    ws.getCell(r, 1).font = { bold: true, size: 13, color: { argb: BRAND_NAVY_ARGB } };
    r += 1;
    ws.getCell(r, 1).value = `Folha de Ponto — ${s.name}`;
    ws.getCell(r, 1).font = { size: 10, color: { argb: 'FF505050' } };
    r += 1;
    ws.getCell(r, 1).value = per;
    ws.getCell(r, 1).font = { size: 9, color: { argb: 'FF888888' } };
    r += 1;
    ws.getCell(r, 1).value = gen;
    ws.getCell(r, 1).font = { size: 8, italic: true, color: { argb: 'FF9AA3AF' } };
    r += 2; // linha em branco

    r = xlsxSectionHeader(ws, r, s.name.toUpperCase(), s.headers.length);

    const headerRow = r;
    s.headers.forEach((h, i) => {
      const cell = ws.getCell(headerRow, i + 1);
      cell.value = h;
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_NAVY_LIGHT_ARGB } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });
    r += 1;

    s.rows.forEach((row) => {
      row.forEach((value, i) => {
        const cell = ws.getCell(r, i + 1);
        cell.value = value;
        styleDataCell(cell, s.headers[i], value);
        if (i === 0) cell.font = { ...(cell.font || {}), bold: true };
      });
      r += 1;
    });

    // Zebra striping leve, igual ao padrão do PDF (alternateRowStyles)
    for (let row = headerRow + 1; row < r; row++) {
      if ((row - headerRow) % 2 === 0) {
        for (let c = 1; c <= s.headers.length; c++) {
          const cell = ws.getCell(row, c);
          if (!cell.fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FB' } };
        }
      }
    }

    // Cabeçalho da tabela sempre visível ao rolar + filtro pronto pra uso
    const lastDataRow = r - 1;
    if (lastDataRow >= headerRow) {
      ws.autoFilter = { from: { row: headerRow, column: 1 }, to: { row: lastDataRow, column: s.headers.length } };
    }
    ws.views = [{ state: 'frozen', ySplit: headerRow }];

    // Largura automática das colunas
    s.headers.forEach((h, i) => {
      const maxLen = Math.max(
        h.length,
        ...s.rows.map((row) => String(row[i] ?? '').length),
      );
      ws.getColumn(i + 1).width = Math.min(Math.max(maxLen + 2, 14), 55);
    });
  }

  const filename = `folha-ponto-${new Date().toISOString().slice(0, 10)}.xlsx`;
  await downloadWorkbook(wb, filename);
}

// ─── Exportação PDF ───────────────────────────────────────────────────────────

// Colunas com valores monetários/horas extras recebem realce vermelho
const OVERTIME_COLS = [
  'Horas Extras',
  'H.E. Diárias (>8h/dia)',
  'H.E. Semanais (>44h/sem)',
  'Total Horas Extras',
];

// Índices (0-based) das colunas de alertas
function alertColIndex(headers) {
  return headers.findIndex((h) => h.includes('Alerta') || h.includes('Ocorrência'));
}

async function buildPDF(title, headers, rows, filters) {
  // Carga dinâmica para não inflar o bundle inicial
  const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);

  const doc   = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const logoB64 = await getLogoBase64();

  // ── Cabeçalho ──
  const cursorY = drawPdfHeader(doc, {
    logoB64,
    subtitle: `Folha de Ponto — ${title}`,
    meta: periodLabel(filters),
    pageW,
  });

  const alertIdx = alertColIndex(headers);

  // ── Tabela ──
  autoTable(doc, {
    startY: cursorY,
    head: [headers],
    body: rows,
    margin: { left: 10, right: 10 },
    tableLineWidth: 0.2,
    tableLineColor: [210, 215, 220],
    styles: {
      fontSize: 7.8,
      cellPadding: { top: 3, right: 4, bottom: 3, left: 4 },
      textColor: [50, 50, 50],
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: BRAND_NAVY_RGB,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 7.8,
    },
    alternateRowStyles: {
      fillColor: [248, 249, 251],
    },
    didParseCell(data) {
      if (data.section !== 'body') return;
      const h   = headers[data.column.index];
      const val = String(data.cell.raw ?? '');

      // Colunas de HE: vermelho se tiver valor real
      if (OVERTIME_COLS.includes(h) && val !== '—' && val !== '') {
        data.cell.styles.textColor = RED_RGB;
        data.cell.styles.fontStyle = 'bold';
      }
      // Coluna de alertas: verde para OK, vermelho para alertas
      if (data.column.index === alertIdx) {
        if (val === 'OK') {
          data.cell.styles.textColor = GREEN_RGB;
        } else if (val !== '—') {
          data.cell.styles.textColor = RED_RGB;
          data.cell.styles.fontStyle = 'bold';
        }
      }
      // Primeira coluna (Funcionário): negrito
      if (data.column.index === 0) {
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });

  // ── Rodapé em todas as páginas ──
  drawPdfFooter(doc, `${COMPANY} · Folha de Ponto · ${title}`);

  return doc;
}

// ─── Espelho de Ponto Individual (Portaria MTE 671/2021, art. 83–84) ─────────

export async function exportEspelhoIndividual(empData, records, filters, { chartEl } = {}) {
  const [{ default: jsPDF }, { default: autoTable }, chart] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
    captureChartImage(chartEl),
  ]);

  const doc    = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW  = doc.internal.pageSize.getWidth();
  const logoB64 = await getLogoBase64();
  const margin = { left: 10, right: 10 };
  const contentWidth = pageW - margin.left - margin.right;

  // ── Cabeçalho ──
  let cursorY = drawPdfHeader(doc, {
    logoB64,
    subtitle: 'Espelho de Ponto Eletrônico — Portaria MTE 671/2021, Art. 84',
    meta: `${empData.full_name || '—'} · ${empData.department || '—'} · ${periodLabel(filters)}`,
    pageW,
  });

  const s = empData.summary || {};

  // ── Dados do colaborador ──
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(120, 128, 138);
  doc.text(`MATRÍCULA: ${empData.registration || '—'}`, margin.left, cursorY);
  cursorY += 5;

  // ── Resumo do período — cards de KPI (mesmo conjunto exibido na tela) ──
  cursorY = drawKpiCardsPdf(doc, {
    startY: cursorY,
    pageW,
    perRow: 3,
    cards: [
      { label: 'Dias Trabalhados', value: String(s.complete_records || 0) },
      { label: 'Pontualidade', value: s.punctuality_pct != null ? `${s.punctuality_pct}%` : '—' },
      { label: 'Total Trabalhado', value: s.total_worked_fmt || '—' },
      { label: 'Horas Extras', value: s.total_overtime_fmt || '—', color: (s.total_overtime_minutes > 0) ? RED_RGB : null },
      { label: 'Alertas CLT', value: s.total_violations > 0 ? `${s.total_violations} alerta(s)` : 'Nenhum', color: s.total_violations > 0 ? RED_RGB : GREEN_RGB },
    ],
  });

  // ── Gráfico: horas extras por dia ──
  if (chart) {
    const imgH = contentWidth * (chart.height / chart.width);
    cursorY = ensurePdfSpace(doc, cursorY, 7 + 4 + imgH + 8);
    cursorY = drawSectionBarPdf(doc, { y: cursorY, width: contentWidth, text: 'HORAS EXTRAS POR DIA' });
    cursorY = addChartImagePdf(doc, { chart, y: cursorY, maxWidth: contentWidth });
  }

  // ── Tabela de registros ──
  const empRecords = records.filter((r) => r.employee_id === empData.id || r.employee?.id === empData.id);

  const rows = empRecords.map((r) => [
    r.work_date ? fmtDateBR(r.work_date) : '—',
    r.nsr ? String(r.nsr).padStart(9, '0') : '—',
    fmtTime(r.clock_in),
    fmtTime(r.lunch_out),
    fmtTime(r.lunch_in),
    fmtTime(r.clock_out),
    r.incomplete ? 'Em aberto' : (r.worked_fmt  || '—'),
    r.overtime_minutes > 0 ? r.overtime_fmt : '—',
    r.violations?.length > 0 ? r.violations.join('; ') : (r.has_night_work ? 'Noturno' : 'OK'),
  ]);

  const headers = ['Data', 'NSR', 'Entrada', 'S. Almoço', 'Ret. Almoço', 'Saída', 'Trabalhado', 'H. Extra', 'Ocorrências'];

  cursorY = ensurePdfSpace(doc, cursorY, 20);
  cursorY = drawSectionBarPdf(doc, { y: cursorY, width: contentWidth, text: 'REGISTROS DE PONTO' });

  autoTable(doc, {
    startY: cursorY,
    head: [headers],
    body: rows,
    margin,
    styles: { fontSize: 7.8, cellPadding: { top: 2.5, right: 3, bottom: 2.5, left: 3 }, textColor: [50, 50, 50] },
    headStyles: { fillColor: [46, 64, 84], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7.8 },
    alternateRowStyles: { fillColor: [248, 249, 251] },
    didParseCell(data) {
      if (data.section !== 'body') return;
      const val = String(data.cell.raw ?? '');
      if (data.column.index === 7 && val !== '—' && val !== '') {
        data.cell.styles.textColor = RED_RGB;
        data.cell.styles.fontStyle = 'bold';
      }
      if (data.column.index === 8) {
        if (val === 'OK') data.cell.styles.textColor = GREEN_RGB;
        else if (val !== '—') { data.cell.styles.textColor = RED_RGB; data.cell.styles.fontStyle = 'bold'; }
      }
    },
  });

  // ── Linha de assinatura ──
  const finalY = doc.lastAutoTable.finalY + 14;
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.3);
  doc.line(10, finalY, pageW / 2 - 5, finalY);
  doc.line(pageW / 2 + 5, finalY, pageW - 10, finalY);

  doc.setFontSize(7.5);
  doc.setTextColor(130, 130, 130);
  doc.text('Assinatura do Empregador', 10, finalY + 4);
  doc.text('Assinatura do Colaborador', pageW / 2 + 5, finalY + 4);

  doc.setFontSize(7);
  doc.setTextColor(160, 160, 160);
  doc.text('Documento gerado conforme Portaria MTE 671/2021, Art. 83 e 84', pageW / 2, finalY + 10, { align: 'center' });

  // ── Rodapé paginado ──
  drawPdfFooter(doc, `${COMPANY} · Espelho de Ponto · ${empData.full_name}`);

  doc.save(`espelho-ponto-${empData.full_name.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ─── Espelho de Ponto — exportação Excel ─────────────────────────────────────

export async function exportEspelhoExcel(empData, records, filters, { chartEl } = {}) {
  const [{ default: ExcelJS }, chart] = await Promise.all([
    import('exceljs'),
    captureChartImage(chartEl),
  ]);

  const empRecords = records.filter((r) => r.employee_id === empData.id || r.employee?.id === empData.id);
  const s = empData.summary || {};

  const headers = ['Data', 'NSR', 'Entrada', 'Saída Almoço', 'Retorno Almoço', 'Saída', 'Trabalhado', 'H. Extra', 'Ocorrências'];
  const rows = empRecords.map((r) => [
    r.work_date ? fmtDateBR(r.work_date) : '—',
    r.nsr ? String(r.nsr).padStart(9, '0') : '—',
    fmtTime(r.clock_in),
    fmtTime(r.lunch_out),
    fmtTime(r.lunch_in),
    fmtTime(r.clock_out),
    r.incomplete ? 'Em aberto' : (r.worked_fmt  || '—'),
    r.overtime_minutes > 0 ? r.overtime_fmt : '—',
    r.violations?.length > 0 ? r.violations.join('; ') : (r.has_night_work ? 'Noturno' : 'OK'),
  ]);

  const wb = new ExcelJS.Workbook();
  wb.creator = COMPANY;
  wb.created = new Date();
  const ws = wb.addWorksheet('Espelho de Ponto');
  const logoB64 = await getLogoBase64();

  let r = 1;
  if (logoB64) {
    const imgId = wb.addImage({ base64: logoB64, extension: 'jpeg' });
    ws.addImage(imgId, { tl: { col: 0, row: 0 }, ext: { width: 96, height: 55 } });
    r = 6;
  }

  ws.getCell(r, 1).value = COMPANY;
  ws.getCell(r, 1).font = { bold: true, size: 13, color: { argb: BRAND_NAVY_ARGB } };
  r += 1;
  ws.getCell(r, 1).value = 'Espelho de Ponto Eletrônico — Portaria MTE 671/2021, Art. 84';
  ws.getCell(r, 1).font = { size: 10, color: { argb: 'FF505050' } };
  r += 1;
  ws.getCell(r, 1).value = `${empData.full_name || '—'} · ${empData.department || '—'} · Matrícula ${empData.registration || '—'} · ${periodLabel(filters)}`;
  ws.getCell(r, 1).font = { size: 9, color: { argb: 'FF888888' } };
  r += 1;
  ws.getCell(r, 1).value = genLabel();
  ws.getCell(r, 1).font = { size: 8, italic: true, color: { argb: 'FF9AA3AF' } };
  r += 2;

  // ── Resumo do período — cards de KPI ──
  r = xlsxSectionHeader(ws, r, 'RESUMO DO PERÍODO', headers.length);
  r = xlsxKpiCards(ws, r, [
    { label: 'Dias Trabalhados', value: s.complete_records || 0 },
    { label: 'Pontualidade', value: s.punctuality_pct != null ? `${s.punctuality_pct}%` : '—' },
    { label: 'Total Trabalhado', value: s.total_worked_fmt || '—' },
    { label: 'Horas Extras', value: s.total_overtime_fmt || '—', colorArgb: s.total_overtime_minutes > 0 ? RED_ARGB : undefined },
    { label: 'Alertas CLT', value: s.total_violations > 0 ? `${s.total_violations} alerta(s)` : 'Nenhum', colorArgb: s.total_violations > 0 ? RED_ARGB : GREEN_ARGB },
  ], { totalCols: headers.length, perRow: 3 });
  r += 1;

  // ── Gráfico: horas extras por dia ──
  if (chart) {
    r = xlsxSectionHeader(ws, r, 'HORAS EXTRAS POR DIA', headers.length);
    r = addChartImageXlsx(wb, ws, { chart, row: r, widthPx: 600 });
    r += 1;
  }

  r = xlsxSectionHeader(ws, r, 'REGISTROS DE PONTO', headers.length);
  const headerRow = r;
  headers.forEach((h, i) => {
    const cell = ws.getCell(headerRow, i + 1);
    cell.value = h;
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_NAVY_LIGHT_ARGB } };
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  });
  r += 1;

  // Índices de coluna (0-based) das colunas H. Extra e Ocorrências — mesma
  // lógica condicional (por índice, não por nome) usada em
  // `exportEspelhoIndividual` (PDF) via didParseCell.
  rows.forEach((row) => {
    row.forEach((value, i) => {
      const cell = ws.getCell(r, i + 1);
      cell.value = value;
      const val = String(value ?? '');
      if (i === 7 && val !== '—' && val !== '') {
        cell.font = { color: { argb: RED_ARGB }, bold: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SEMAFORO_TINT_ARGB.vermelho } };
      } else if (i === 8) {
        if (val === 'OK') {
          cell.font = { color: { argb: GREEN_ARGB } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SEMAFORO_TINT_ARGB.verde } };
        } else if (val !== '—') {
          cell.font = { color: { argb: RED_ARGB }, bold: true };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SEMAFORO_TINT_ARGB.vermelho } };
        }
      }
      if (i === 0) cell.font = { ...(cell.font || {}), bold: true };
    });
    if ((r - headerRow) % 2 === 0) {
      for (let c = 1; c <= headers.length; c++) {
        const cell = ws.getCell(r, c);
        if (!cell.fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FB' } };
      }
    }
    r += 1;
  });

  const lastDataRow = r - 1;
  if (lastDataRow >= headerRow) {
    ws.autoFilter = { from: { row: headerRow, column: 1 }, to: { row: lastDataRow, column: headers.length } };
  }
  ws.views = [{ state: 'frozen', ySplit: headerRow }];

  headers.forEach((h, i) => {
    const maxLen = Math.max(h.length, ...rows.map((row) => String(row[i] ?? '').length));
    ws.getColumn(i + 1).width = Math.min(Math.max(maxLen + 2, 14), 55);
  });

  const safeName = (empData.full_name || 'funcionario').replace(/\s+/g, '-').toLowerCase();
  await downloadWorkbook(wb, `espelho-ponto-${safeName}-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

// ─── Comprovante de Marcação (Portaria MTE 671/2021) ─────────────────────────

export async function exportComprovante(record, eventType, eventTime) {
  const [{ default: jsPDF }] = await Promise.all([import('jspdf')]);

  const doc    = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a5' });
  const pageW  = doc.internal.pageSize.getWidth();   // 148 mm
  const pageH  = doc.internal.pageSize.getHeight();  // 210 mm
  const logoB64 = await getLogoBase64();

  // ── Cabeçalho ──
  const headerEndY = drawPdfHeader(doc, {
    logoB64,
    subtitle: 'Comprovante de Registro de Ponto',
    meta: 'Portaria MTE 671/2021',
    pageW,
    height: 26,
  });

  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(10, headerEndY + 3, pageW - 10, headerEndY + 3);

  // ── Dados do colaborador ──
  const emp = record.employee || {};
  const infoRows = [
    ['Funcionário',  emp.full_name             || '—'],
    ['Departamento', emp.department?.name      || '—'],
    ['Matrícula',    emp.registration          || '—'],
  ];

  let y = headerEndY + 10;
  doc.setFontSize(8.5);
  for (const [label, val] of infoRows) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(90, 90, 90);
    doc.text(`${label}:`, 10, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(31, 45, 61);
    doc.text(val, 46, y);
    y += 6;
  }

  // ── Caixa da marcação ──
  const boxY = y + 4;
  doc.setFillColor(244, 250, 220);
  doc.setDrawColor(175, 217, 24);
  doc.setLineWidth(0.5);
  doc.roundedRect(10, boxY, pageW - 20, 46, 3, 3, 'FD');

  const evtDate = record.work_date
    ? new Date(record.work_date + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', year: 'numeric', month: 'long', day: '2-digit' })
    : '—';
  const evtTimeStr = eventTime
    ? new Date(eventTime).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '—';
  const nsr = record.nsr ? String(record.nsr).padStart(9, '0') : '—';

  const boxRows = [
    ['Tipo de Marcação', eventType],
    ['Data',             evtDate],
    ['Horário',          evtTimeStr],
    ['NSR',              nsr],
  ];

  let by = boxY + 10;
  for (const [label, val] of boxRows) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(90, 90, 90);
    doc.text(`${label}:`, 16, by);
    doc.setFont('helvetica', label === 'Horário' ? 'bold' : 'normal');
    doc.setFontSize(label === 'Horário' ? 12 : 9);
    doc.setTextColor(31, 45, 61);
    doc.text(val, 62, by);
    by += label === 'Horário' ? 9 : 8;
  }

  // ── Código de verificação ──
  const verCode = btoa(`${nsr}|${record.work_date}|${eventType}`).slice(0, 24).toUpperCase();
  const verY = boxY + 54;

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 120, 120);
  doc.text('Código de Verificação:', 10, verY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(60, 60, 60);
  doc.text(verCode, 10, verY + 5);

  // ── Rodapé (mini-marca consistente com o cabeçalho) ──
  doc.setFillColor(...BRAND_GREEN_RGB);
  doc.rect(pageW / 2 - 4, pageH - 25, 8, 1, 'F');

  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(10, pageH - 22, pageW - 10, pageH - 22);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(160, 160, 160);
  doc.text('Documento gerado conforme Portaria MTE 671/2021', pageW / 2, pageH - 17, { align: 'center' });
  doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, pageW / 2, pageH - 12, { align: 'center' });
  doc.text('Assinatura PAdES requer integração com certificado digital ICP-Brasil', pageW / 2, pageH - 7, { align: 'center' });

  const safeName = (emp.full_name || 'funcionario').replace(/\s+/g, '-').toLowerCase();
  const safeType = eventType.replace(/\s+/g, '-').replace(/[^\w-]/g, '').toLowerCase();
  doc.save(`comprovante-${safeType}-${safeName}-${record.work_date || 'sem-data'}.pdf`);
}

export async function exportPDF(records, reportData, filters, activeTab) {
  const tabConfig = {
    diario:  { title: 'Registros Diários', headers: DAILY_HEADERS,   rows: dailyRows(records) },
    semanal: { title: 'Resumo Semanal',    headers: WEEKLY_HEADERS,  rows: weeklyRows(reportData) },
    mensal:  { title: 'Resumo Mensal',     headers: MONTHLY_HEADERS, rows: monthlyRows(reportData) },
  };

  const cfg = tabConfig[activeTab] || tabConfig.mensal;
  const doc = await buildPDF(cfg.title, cfg.headers, cfg.rows, filters);
  const filename = `folha-ponto-${activeTab}-${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}
